import { Add, InfoOutlined, Link } from '@mui/icons-material';
import { Box, Button, Divider, IconButton, MenuItem, Select, Stack, Tooltip, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { dispatchSiteAssociationActions } from '@sophocles/api/odlApp';
import { useUser } from '@sophocles/app';
import SiteGroupAutocomplete from '@sophocles/components/SiteGroupAutocomplete';
import { selectCustomer } from '@sophocles/features/auth/authSelectors';
import { loadThirdParties } from '@sophocles/features/customers/api';
import { selectThirdPartiesByCustomerId } from '@sophocles/features/customers/slice';
import { selectSiteData } from '@sophocles/features/entities/site';
import { addToast } from '@sophocles/features/toast';
import {
  createAutoShares,
  loadAutoShares,
  loadVirtualMemberships,
  removeAutoShares,
} from '@sophocles/features/virtual/api';
import { selectAutoSharesBySiteGroupId, selectVirtualMembershipsBySiteId } from '@sophocles/features/virtual/slice';
import { SettingsSubsection } from '@sophocles/views/settings/components';

// Shared row layout — vendor / destination columns with edge-to-edge
// dividers between rows.  Compact 8/16 padding, no explicit min-height
// so content drives the row size (form-control rows naturally settle
// at ~40px, plain-text rows hug their typography).
function ShareRow({ left, right, last = false }) {
  return (
    <Stack
      direction="row"
      spacing={2}
      alignItems="center"
      sx={{
        py: 1,
        px: 2,
        borderBottom: last ? 0 : 1,
        borderColor: 'divider',
      }}
    >
      <Box sx={{ flex: '0 0 40%', minWidth: 0, display: 'flex', alignItems: 'center', gap: 0.5 }}>{left}</Box>
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 1 }}>{right}</Box>
    </Stack>
  );
}

function ThirdPartiesTab({ customerId, siteId = null, siteGroup = null, onChange = null, resetPendingVersion = 0 }) {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const isSiteGroupSharing = siteId == null;

  const customer = useSelector(selectCustomer(customerId));
  const siteData = useSelector(selectSiteData(siteId));
  // is_billboard from the site snapshot when a siteId is present; falls back to
  // the legacy customer entity for the site-group-level sharing path (siteId=null).
  const isBillboard = siteId != null ? (siteData?.is_billboard ?? false) : (customer?.is_billboard ?? false);
  // Inline term resolution — a shared tenant-terms hook would remove this
  // duplication once one is extracted for the StructureCard area.
  const siteGroupTerm = t('siteGroups.siteGroup');
  const siteGroupsTerm = t('siteGroups.siteGroups');

  // siteId != null path reads owner from the site snapshot; siteId == null
  // (site-group-level sharing) has no site snapshot, so the caller passes
  // the site group directly via the `siteGroup` prop.
  const parentSgId = siteId != null && siteData?.owner != null ? siteData.owner.id : siteGroup?.id;
  const siteGroupDescription =
    siteId != null && siteData?.owner != null ? siteData.owner.description : siteGroup?.description;
  const { isStaff } = useUser();

  const thirdParties = useSelector(selectThirdPartiesByCustomerId(customerId));
  const virtualMemberships = useSelector(selectVirtualMembershipsBySiteId(siteId));
  const autoShares = useSelector(selectAutoSharesBySiteGroupId(parentSgId));

  const [thirdPartyAdditions, setThirdPartyAdditions] = useState([]);
  const [thirdPartyRemovals, setThirdPartyRemovals] = useState([]);

  const [internalAdditions, setInternalAdditions] = useState([]);
  const [internalRemovals, setInternalRemovals] = useState([]);
  const previousResetPendingVersion = React.useRef(resetPendingVersion);

  useEffect(() => {
    if (previousResetPendingVersion.current === resetPendingVersion) return;
    previousResetPendingVersion.current = resetPendingVersion;
    setThirdPartyAdditions([]);
    setThirdPartyRemovals([]);
    setInternalAdditions([]);
    setInternalRemovals([]);
  }, [resetPendingVersion]);

  useEffect(() => {
    if (!isSiteGroupSharing) {
      dispatch(loadVirtualMemberships({ site: siteId }));
    } else {
      dispatch(loadAutoShares({ site_group_id: parentSgId }));
    }
    dispatch(loadThirdParties({ customerId }));
  }, [dispatch, siteId, customerId]);

  const customerName = thirdParties?.[0]?.owner?.name ?? t('sites.unknownCustomer', 'Customer');
  const siteGroupName = siteGroupDescription;

  const getChanges = () => {
    const newlyShared = [];
    const newlyUnshared = [];

    // External removals
    thirdPartyRemovals.forEach(vm => {
      if (isSiteGroupSharing) {
        newlyUnshared.push({
          id: vm.id,
        });
      } else {
        newlyUnshared.push({
          id: vm.id,
          site_id: siteId,
          third_party_site_group_id: vm.third_party.site_group?.id || null,
          third_party_id: vm.third_party.id,
        });
      }
    });

    // External additions
    thirdPartyAdditions
      .filter(add => add.third_party_id && add.virtual_format)
      .forEach(add => {
        if (isSiteGroupSharing) {
          newlyShared.push({
            site_group_id: parentSgId,
            third_party_id: add.third_party_id,
            virtual_format: add.virtual_format,
          });
        } else {
          newlyShared.push({
            site_id: siteId,
            third_party_id: add.third_party_id,
            virtual_format: add.virtual_format,
          });
        }
      });

    // Internal additions
    internalAdditions.forEach(siteGroupId => {
      if (siteGroupId) {
        if (isSiteGroupSharing) {
          newlyShared.push({
            site_group_id: siteGroupId,
            third_party_id: customerId,
            third_party_site_group_id: siteGroupId,
            virtual_format: 'CUSTOM',
          });
        } else {
          newlyShared.push({
            site_id: siteId,
            third_party_id: customerId,
            third_party_site_group_id: siteGroupId,
            virtual_format: 'CUSTOM',
          });
        }
      }
    });

    internalRemovals.forEach(siteGroupId => {
      if (siteGroupId) {
        if (isSiteGroupSharing) {
          newlyUnshared.push({
            id: siteGroupId,
          });
        } else {
          // Look up the membership id for the per-site associations unshare path.
          const vm = virtualMemberships.find(m => m.third_party.site_group?.id === siteGroupId);
          newlyUnshared.push({
            id: vm?.id ?? null,
            site_id: siteId,
            third_party_site_group_id: siteGroupId,
            third_party_id: customerId,
          });
        }
      }
    });

    return { newlyShared, newlyUnshared };
  };

  // Count how many times each vendor is already shared with
  const vendorSharingCounts = new Map();

  // Count existing shares by vendor (excluding ones marked for removal)
  (isSiteGroupSharing ? autoShares : virtualMemberships)
    .filter(vm => {
      // Exclude self
      if (vm.third_party.id === customerId) return false;

      // Exclude ones marked for removal
      const isMarkedForRemoval = thirdPartyRemovals.some(removal => removal.id === vm.id);

      return !isMarkedForRemoval;
    })
    .forEach(vm => {
      const vendorName = vm.third_party.name;
      vendorSharingCounts.set(vendorName, (vendorSharingCounts.get(vendorName) || 0) + 1);
    });

  // Count pending additions by vendor
  thirdPartyAdditions
    .filter(add => add.third_party_id)
    .forEach(add => {
      const thirdParty = thirdParties.find(tp => tp.third_party.id === add.third_party_id);
      if (thirdParty) {
        const vendorName = thirdParty.third_party.name;
        vendorSharingCounts.set(vendorName, (vendorSharingCounts.get(vendorName) || 0) + 1);
      }
    });

  const availableThirdParties = thirdParties.filter(tp => {
    // Exclude self
    if (tp.third_party.id === customerId) return false;

    // Restrict sharing to same vendor to maximum of 2 times
    const vendorName = tp.third_party.name;
    const currentCount = vendorSharingCounts.get(vendorName) || 0;
    return currentCount < 2;
  });

  useEffect(() => {
    if (onChange) {
      onChange(getChanges());
    }
  }, [thirdPartyAdditions, thirdPartyRemovals, internalAdditions, internalRemovals]);

  const submit = () => {
    if (isSiteGroupSharing) {
      // Site-group-level sharing: keep on legacy auto-shares API.
      const { newlyShared, newlyUnshared } = getChanges();
      if (newlyShared.length > 0) {
        dispatch(createAutoShares(newlyShared));
      }
      if (newlyUnshared.length > 0) {
        dispatch(removeAutoShares(newlyUnshared.map(u => u.id)));
      }
    } else {
      // Per-site sharing: use associations dispatch endpoint.
      const commands = [];

      // External additions — no site_group ref available in the form, keep on legacy.
      const externalShares = thirdPartyAdditions.filter(add => add.third_party_id && add.virtual_format);
      if (externalShares.length > 0) {
        dispatch(
          createAutoShares(
            externalShares.map(add => ({
              site_id: siteId,
              third_party_id: add.third_party_id,
              virtual_format: add.virtual_format,
            })),
          ),
        );
      }

      // Internal additions — scope: internal, site_group from the selected SG id.
      internalAdditions.forEach(sgId => {
        if (sgId) {
          commands.push({ key: 'share', params: { scope: 'internal', site_group: { type: 'site-group', id: sgId } } });
        }
      });

      // External unshares — vm.id is the membership id.
      thirdPartyRemovals.forEach(vm => {
        commands.push({ key: 'unshare', params: { membership: { type: 'site-group-membership', id: vm.id } } });
      });

      // Internal unshares — look up the membership id from stored SG id.
      internalRemovals.forEach(sgId => {
        const vm = virtualMemberships.find(m => m.third_party.site_group?.id === sgId);
        if (vm) {
          commands.push({ key: 'unshare', params: { membership: { type: 'site-group-membership', id: vm.id } } });
        }
      });

      if (commands.length > 0) {
        const SUCCESS_STATUSES = ['queued', 'applied'];
        dispatchSiteAssociationActions(siteId, commands)
          .then(batchResult => {
            // A 207 partial-success resolves here — inspect each entry for failures.
            const failed = Array.isArray(batchResult)
              ? batchResult.filter(entry => !SUCCESS_STATUSES.includes(entry?.status))
              : [];
            if (failed.length > 0) {
              const first = failed[0];
              const message = first?.message_key ? t(first.message_key) : (first?.detail ?? null);
              dispatch(
                addToast({
                  title: t('general.error', 'Error'),
                  message: message ?? t('sites.sharingUpdateFailed', 'Failed to update sharing. Please try again.'),
                  level: 'error',
                  dismissible: true,
                }),
              );
            } else {
              dispatch(
                addToast({
                  title: t('general.success', 'Success'),
                  message: t('sites.sharingUpdated', 'Sharing updated'),
                  level: 'success',
                  dismissible: true,
                }),
              );
            }
          })
          .catch(() => {
            dispatch(
              addToast({
                title: t('general.error', 'Error'),
                message: t('sites.sharingUpdateFailed', 'Failed to update sharing. Please try again.'),
                level: 'error',
                dismissible: true,
              }),
            );
          });
      }
    }

    setThirdPartyAdditions([]);
    setThirdPartyRemovals([]);
    setInternalAdditions([]);
    setInternalRemovals([]);
  };

  // ─── External shares list ─────────────────────────────────────
  const externalExisting = (isSiteGroupSharing ? autoShares : virtualMemberships).filter(
    vm => vm.third_party.id != customerId && !thirdPartyRemovals.some(removal => removal.id === vm.id),
  );
  const externalRowCount = externalExisting.length + thirdPartyAdditions.length;
  const externalIsEmpty = externalRowCount === 0;
  const externalAddDisabled = availableThirdParties.length === 0;

  // ─── Internal shares list ─────────────────────────────────────
  const internalExisting = (isSiteGroupSharing ? autoShares : virtualMemberships).filter(
    vm => vm.third_party.id === customerId && !internalRemovals.includes(vm.third_party.site_group.id),
  );
  const internalRowCount = internalExisting.length + internalAdditions.length;
  const internalIsEmpty = internalRowCount === 0;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* ─────────── External Shares Panel ─────────── */}
      <SettingsSubsection
        title={t('sites.externalShares', 'External Shares')}
        description={t(
          'sites.externalSharesDescription',
          'Grant another customer read-only access. Each row picks the vendor and how this site appears to them.',
        )}
        bordered
        flush
      >
        <Stack divider={<Divider />}>
          {externalIsEmpty ? (
            <Box sx={{ px: 2, py: 1.5, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                {t('sites.noExternalShares', 'No external shares yet.')}
              </Typography>
            </Box>
          ) : (
            <>
              {/* Column hint row — only when there's at least one row to label */}
              <ShareRow
                left={
                  <Tooltip title={t('sites.thirdPartyHelp')}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.6,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        cursor: 'help',
                      }}
                    >
                      {t('sites.thirdParty')}
                      <InfoOutlined sx={{ fontSize: 14 }} color="action" />
                    </Typography>
                  </Tooltip>
                }
                right={
                  <Tooltip title={t('sites.destinationHelp')}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.6,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        cursor: 'help',
                      }}
                    >
                      {t('sites.destination')}
                      <InfoOutlined sx={{ fontSize: 14 }} color="action" />
                    </Typography>
                  </Tooltip>
                }
              />
              {externalExisting.map(vm => {
                const mappedName = vm.third_party.site_group?.description ?? t('general.unknown', 'Unknown');
                const openSite = () => {
                  const base = `/c/${vm.third_party.id}/plant/${vm.third_party.site_group.id}`;
                  window.location.href = isSiteGroupSharing ? `${base}/structures` : `${base}/structure/${siteId}`;
                };
                return (
                  <ShareRow
                    key={vm.id}
                    left={<Typography variant="body2">{vm.third_party.name}</Typography>}
                    right={
                      <>
                        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" noWrap>
                            {mappedName}
                          </Typography>
                          {isStaff && (
                            <IconButton size="small" sx={{ ml: 0.5 }} onClick={openSite}>
                              <Link fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                        <Button
                          size="small"
                          variant="text"
                          color="error"
                          onClick={() => setThirdPartyRemovals(prev => [...prev, vm])}
                        >
                          {t('actions.unshare')}
                        </Button>
                      </>
                    }
                  />
                );
              })}
              {thirdPartyAdditions.map((add, idx) => (
                <ShareRow
                  key={`add-${idx}`}
                  left={
                    <Select
                      size="small"
                      fullWidth
                      displayEmpty
                      hiddenLabel
                      value={add.third_party_id ?? ''}
                      onChange={e => {
                        const updated = [...thirdPartyAdditions];
                        updated[idx].third_party_id = e.target.value;
                        setThirdPartyAdditions(updated);
                      }}
                    >
                      <MenuItem value="" disabled>
                        {t('sites.selectThirdParty')}
                      </MenuItem>
                      {add.third_party_id && (
                        <MenuItem value={add.third_party_id}>
                          {thirdParties.find(tp => tp.third_party.id === add.third_party_id)?.third_party.name ||
                            t('general.unknown', 'Unknown')}
                        </MenuItem>
                      )}
                      {availableThirdParties.map(tp => (
                        <MenuItem key={tp.third_party.id} value={tp.third_party.id}>
                          {tp.third_party.name}
                        </MenuItem>
                      ))}
                    </Select>
                  }
                  right={
                    <>
                      <Select
                        size="small"
                        fullWidth
                        displayEmpty
                        hiddenLabel
                        value={add.virtual_format || ''}
                        onChange={e => {
                          const updated = [...thirdPartyAdditions];
                          updated[idx].virtual_format = e.target.value;
                          setThirdPartyAdditions(updated);
                        }}
                      >
                        <MenuItem value="" disabled>
                          {t('actions.selectDestination')}
                        </MenuItem>
                        <MenuItem value="CUSTOMER_NAME">{customerName}</MenuItem>
                        <MenuItem value="CUSTOMER_NAME_AND_SITE_GROUP">
                          {customerName} / {siteGroupName}
                        </MenuItem>
                      </Select>
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => setThirdPartyAdditions(thirdPartyAdditions.filter((_, i) => i !== idx))}
                      >
                        {t('actions.cancel')}
                      </Button>
                    </>
                  }
                />
              ))}
            </>
          )}
          <Box sx={{ px: 2, py: 0.75, display: 'flex', justifyContent: 'flex-start' }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<Add />}
              onClick={() => setThirdPartyAdditions(prev => [...prev, { third_party_id: null, virtual_format: '' }])}
              disabled={externalAddDisabled}
            >
              {t('actions.addThirdPartyShare')}
            </Button>
          </Box>
        </Stack>
      </SettingsSubsection>

      {/* ─────────── Internal Shares Panel ─────────── */}
      <SettingsSubsection
        title={t('sites.internalShares', 'Internal Shares')}
        description={t(
          'sites.internalSharesDescription',
          'Mirror this site into another of your own {{groupsTerm}} (virtual share).',
          { groupsTerm: siteGroupsTerm.toLowerCase() },
        )}
        bordered
        flush
      >
        <Stack divider={<Divider />}>
          {internalIsEmpty ? (
            <Box sx={{ px: 2, py: 1.5, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                {t('sites.noInternalShares', 'No internal shares yet.')}
              </Typography>
            </Box>
          ) : (
            <>
              {internalExisting.map(mapping => {
                const mappedName = mapping.third_party.site_group.description;
                const openSite = () => {
                  const base = `/c/${mapping.third_party.id}/plant/${mapping.third_party.site_group.id}`;
                  window.location.href = isSiteGroupSharing ? `${base}/structures` : `${base}/structure/${siteId}`;
                };
                return (
                  <ShareRow
                    key={mapping.id}
                    left={
                      <>
                        <Typography variant="body2" noWrap>
                          {mappedName}
                        </Typography>
                        {isStaff && (
                          <IconButton size="small" onClick={openSite}>
                            <Link fontSize="small" />
                          </IconButton>
                        )}
                      </>
                    }
                    right={
                      <>
                        <Box sx={{ flex: 1 }} />
                        <Button
                          size="small"
                          variant="text"
                          color="error"
                          onClick={() => setInternalRemovals([...internalRemovals, mapping.third_party.site_group.id])}
                        >
                          {t('actions.unshare')}
                        </Button>
                      </>
                    }
                  />
                );
              })}
              {internalAdditions.map((add, idx) => (
                <ShareRow
                  key={`add-${idx}`}
                  left={
                    <Typography variant="caption" color="text.secondary">
                      {t('sites.newShare')}
                    </Typography>
                  }
                  right={
                    <>
                      <SiteGroupAutocomplete
                        fullWidth
                        customerId={customerId}
                        label={siteGroupTerm}
                        value={add || null}
                        ignoreIds={[parentSgId]}
                        filters={{ is_virtual: true }}
                        onChange={(e, siteGroupId) => {
                          const updated = [...internalAdditions];
                          updated[idx] = siteGroupId;
                          setInternalAdditions(updated);
                        }}
                      />
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => setInternalAdditions(internalAdditions.filter((_, i) => i !== idx))}
                      >
                        {t('actions.cancel')}
                      </Button>
                    </>
                  }
                />
              ))}
            </>
          )}
          <Box sx={{ px: 2, py: 0.75, display: 'flex', justifyContent: 'flex-start' }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<Add />}
              onClick={() => setInternalAdditions(prev => [...prev, null])}
            >
              {t('actions.addInternalShare')}
            </Button>
          </Box>
        </Stack>
      </SettingsSubsection>
      {onChange == null && (
        <Button
          onClick={submit}
          variant="contained"
          color="primary"
          sx={{ mt: 2 }}
          disabled={
            thirdPartyAdditions.length === 0 &&
            thirdPartyRemovals.length === 0 &&
            internalAdditions.length === 0 &&
            internalRemovals.length === 0
          }
        >
          {t('actions.submit')}
        </Button>
      )}
    </Box>
  );
}

export default ThirdPartiesTab;
